import { styled } from '@material-ui/core';
import React from 'react';

import { ThemeRes } from '../../../../../../Web/Themes/ThemeRes';
import { IRoom } from '../../../../Models/IRoom';
import { PolylabelUtils } from '../../../../Utils/PolylabelUtils';
import { CoordPoint } from '../../../../Utils/Types';
import { useLaizeCalcDialog, useLaizeCalcDialogState } from '../LaizeCalcDialogProvider';
import { isInLaizeCalc } from './MapUIWalls';
import { Message } from 'Locales/I18nService';
import { MapConstants } from 'Plugins/FloorPlan/MapConstants';
import { BUTTON_FLOORING_DIRECTION_WIDTH } from './RoomLaizeDirection/MapUIRoomsButtonFlooringDirection';
import { IconExpandPlanBande } from './Widgets';

export const MapUIRoomInfos = () => {
    const { rooms = [] } = useLaizeCalcDialogState();
    return (
        <g id="mapRoomInfos">
            {rooms.map((room) => {
                const centroid = PolylabelUtils.polylabel([room.coords?.map((p) => [p.x, p.y])], 1.0);
                return <MapUIRoomInfo key={`MapUIRoomInfo-${room.roomId}`} room={room} pos={centroid} />;
            })}
        </g>
    );
};

export type MapUIWidgetProps = { room: IRoom; pos?: CoordPoint } & React.SVGAttributes<SVGElement>;

const MapUIRoomInfo = ({ room, pos }: MapUIWidgetProps) => {
    const { isRoomFocused } = useLaizeBandFocus(room);
    const center = getMapUIRoomAreaNamePosition(pos!);
    const isInLaize = isInLaizeCalc(room);
    center.x = center.x - BUTTON_FLOORING_DIRECTION_WIDTH / 4;
    const transformTranslate = `translate(${center.x},${center.y})`;

    return (
        <g id={`MapUIRoomInfo-${room.roomId}`}>
            <RoomInfo fill={isInLaize ? '#13123A' : 'rgba(0, 0, 0, 0.3)'} transform={transformTranslate}>
                {room.name}
            </RoomInfo>
            <RoomAreaLabel
                room={room}
                pos={center}
                transform={`translate(${center.x},${center.y + nameHeight / 2 + 8})`}
            />
            <g transform={`translate(${center.x - 32},${center.y - 10})`}>
                <IconExpandPlanBande id={`IconExpandPlanBande-${room.roomId}`} focused={isRoomFocused} />
            </g>
        </g>
    );
};

const RoomInfo = styled('text')({
    fontWeight: 900,
    fontSize: 16,
    pointerEvents: 'none',
    fontFamily: ThemeRes.Fonts.fontFamilyNunitoSans,
});

const RoomAreaLabel = ({ room, ...attrs }: MapUIWidgetProps) => {
    const meter = MapConstants.meter;
    const toArea = (area: number) => (area / (meter * meter)).toFixed(2);
    const style: React.CSSProperties = { fontWeight: 400, fontSize: 14, pointerEvents: 'none', touchAction: 'none' };
    return (
        <RoomInfo style={style} fill="#424448" {...attrs}>
            {room.roomItems?.length! > 0 ? (
                <Message
                    id="Room_AreaWithoutItems"
                    values={{
                        area: toArea(room.area!),
                        areaWithoutRoomItems: toArea(room.areaWithoutRoomItems || room.area),
                    }}
                />
            ) : (
                <Message id="Room_Area" values={{ area: toArea(room.area!) }} />
            )}
        </RoomInfo>
    );
};

const nameHeight = 18;
const roomSurfaceHeight = 18;
export const getMapUIRoomAreaNamePosition = (roomCentroid: CoordPoint, withFlooringDirection: boolean = false) => {
    const FlooringDirectionHeight = withFlooringDirection ? 35 : 0;
    const FlooringDirectionMarginTop = withFlooringDirection ? 4 : 0;
    return {
        x: roomCentroid.x,
        y:
            roomCentroid.y -
            nameHeight / 2 -
            roomSurfaceHeight / 2 -
            FlooringDirectionHeight / 2 -
            FlooringDirectionMarginTop / 2,
    };
};

const useLaizeBandFocus = (room: IRoom) => {
    const { focusedBands = {} } = useLaizeCalcDialog();
    const { bandsFocused = [], focusedFrom } = focusedBands;
    const isRoomFocused =
        Boolean(bandsFocused.find((b) => b.roomId === room.roomId)) && focusedFrom === 'FromLaizeBandMap';
    return { isRoomFocused };
};
